import { useMemo } from 'react';

import { Doctor, DoctorFeature } from '../types';
import { getDistanceBetweenPoints } from '../utils/getDistanceBetweenPoints';
import { getDoctorFeatures } from '../utils/getDoctorFeatures';

interface UseDoctorFeaturesProps {
  doctors: Doctor[];
  searchLocation?: { coordinates: [number, number] } | null;
  offsetDistance?: number;
}

export function useDoctorFeatures({
  doctors,
  searchLocation,
  offsetDistance = 0.0001
}: UseDoctorFeaturesProps) {
  const doctorFeatures = useMemo(
    () => getDoctorFeatures(doctors, offsetDistance),
    [doctors, offsetDistance]
  );

  const hasDoctorsNearSearch = useMemo(() => {
    if (!searchLocation) {
      return false;
    }

    if (doctorFeatures.length === 0) {
      return false;
    }

    const searchCoords = searchLocation.coordinates;
    const SEARCH_RADIUS_KM = 5;

    return doctorFeatures.some((feature) => {
      const doctorCoords = feature.geometry.coordinates as [number, number];
      const distance = getDistanceBetweenPoints(searchCoords, doctorCoords);
      return distance <= SEARCH_RADIUS_KM;
    });
  }, [searchLocation, doctorFeatures]);

  const findDoctorFeature = useMemo(() => {
    return (
      doctorName: string,
      longitude: string,
      latitude: string
    ): DoctorFeature | undefined => {
      return doctorFeatures.find(
        (feature) =>
          feature.properties.full_name === doctorName &&
          feature.geometry.coordinates[0] === parseFloat(longitude) &&
          feature.geometry.coordinates[1] === parseFloat(latitude)
      );
    };
  }, [doctorFeatures]);

  return {
    doctorFeatures,
    hasDoctorsNearSearch,
    findDoctorFeature
  };
}
